import { Table } from 'antd'
import { SortOrder } from 'antd/lib/table'
import classNames from 'classnames'
import React, { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PerAvatarPlus } from '../../components/custom_components/AvatarPlus'
import PerButton from '../../components/custom_components/Button'
import { PerEmpty } from '../../components/custom_components/Empty'
import PerSpacer from '../../components/custom_components/Spacer'
import { tableActionButtons } from '../../components/custom_components/TableActionButtons/tableActionButtons.uiprops'
import { PerTag } from '../../components/custom_components/Tag'
import { useAuth } from '../../context/auth-context'
import useCompanyRankingSettingsMutations from '../../graphql/company-ranking-settings/hooks/useCompanyRankingSettingsMutations'
import { NameDisplayPreference } from '../../graphql/enum/name-display-preference.enum'
import useHandleEmployeeTags from '../../graphql/ranking/hooks/useHandleEmployeeTag'
import { computeFullname } from '../../shared/nameDisplayPreferencesUtil'
import { CompanyRankingSettingsDto, Country, EmployeeAndTag, EmployeeDisplayTinyInfo, EmployeeTagType, RoleName } from '../../types'
import { CompanySettingsRankingRemoveLowTagModal } from '../companySettings/ranking/RemoveLowTagModal'
import { LazyRankingTagHistoryModal } from './LazyRankingTagHistoryModal'
import { RankingTableActionButtons } from './RankingTableActionButtons'
import { rankingUiProps } from './ranking.uiprops'
import { AddEntryLevelLowTagAssignmentModal } from '../companySettings/ranking/AddEntryLevelLowTagModal'

type TableData = EmployeeAndTag

type Props = {
  tableData: TableData[]
  companyId: string
  country: Country
  isCompanySettings?: boolean
  settings: CompanyRankingSettingsDto
  isHr?: boolean
  isMessageBoxVisible?: boolean
}

/**
 * This component is related to phase 1.
 * This component uses 'RankingPerformancePopover' component, which has been modified by PER-2863. This leads to an incompatibility w.r.t. phase 1.
 * To safely recover from phase 2 to phase 1, we should restore old version of 'RankingPerformancePopover' and 'RankingTable'
 */
export const RankingTable: FC<Props> = ({
  tableData,
  companyId,
  isCompanySettings = false,
  settings,
  country,
  isHr = false,
  isMessageBoxVisible = false,
}) => {
  const [t] = useTranslation()
  const { user } = useAuth()
  const { removeEmployeeTag, employeeHasFutureEmploymentChange, employeeHasOngoingAppraisal } = useHandleEmployeeTags(
    companyId,
    country,
    undefined,
    settings.tagAssignmentIsInProgress,
  )
  const [modalOpenRemoveLow, setModalOpenRemoveLow] = useState<boolean>(false)
  const [modalDataRemoveLow, setModalDataRemoveLow] = useState<EmployeeAndTag | undefined>(undefined)
  const [modalDataAddEntryLevelLow, setModalDataAddEntryLevelLow] = useState<EmployeeAndTag | undefined>(undefined)
  const [modalOpenTagHistory, setModalOpenTagHistory] = useState<boolean>(false)
  const [modalTagHistoryEmployeeId, setModalTagHistoryEmployeeId] = useState<string | undefined>(undefined)

  const { tableActionButtonsRowWidth } = tableActionButtons()
  const { tagColor, isPerformanceTag, isImpactOfLossTag, isRiskOfLossTag } = rankingUiProps()

  const { updateRankingDisabled } = useCompanyRankingSettingsMutations(companyId, country as Country)

  const provideButtonsRowWidth = () => {
    if (isCompanySettings && isHr) {
      return 3
    } else if (isCompanySettings && !isHr) {
      return 2
    } else {
      return 4
    }
  }

  const onRemoveLowModalCancel = () => {
    setModalOpenRemoveLow(false)
    setModalDataRemoveLow(undefined)
  }

  const onAddEntryLevelLowModalCancel = () => {
    setModalDataAddEntryLevelLow(undefined)
  }

  const onTagHistoryModalCancel = () => {
    setModalOpenTagHistory(false)
    setModalTagHistoryEmployeeId(undefined)
  }

  const columns = [
    {
      key: 'employee',
      title: t('rankingView.table.employee.heading'),
      className: 'minWidth-column-xl textAlign-left',
      sorter: (rowDataA: TableData, rowDataB: TableData) => {
        const nameA = `${rowDataA.firstName} ${rowDataA.lastName}`
        const nameB = `${rowDataB.firstName} ${rowDataB.lastName}`
        return nameA.localeCompare(nameB)
      },
      render: (data: TableData) => (
        <PerAvatarPlus
          nameDisplayPreference={user.nameDisplayPreference as NameDisplayPreference}
          employeeId={data.id}
          firstName={data.firstName}
          lastName={data.lastName}
          infoText={t(`employee.contract.level.${data.currentLevel}`)}
          linkToProfile={
            data.id !== user.employeeId
              ? {
                  resigned: false,
                }
              : undefined
          }
          tooltip={data.id !== user.employeeId ? t('employeeList.table.viewProfile') : undefined}
        />
      ),
    },
    {
      key: 'bu',
      title: t('rankingView.table.businessUnit.heading'),
      className: 'minWidth-column-m textAlign-left',
      sorter: (employeeA: TableData, employeeB: TableData) => {
        return employeeA.buName.localeCompare(employeeB.buName)
      },
      render: (rowData: TableData) => rowData.buName,
    },
    {
      key: 'performance',
      title: t('rankingView.table.performance.heading'),
      className: 'minWidth-column-m textAlign-left',
      defaultSortOrder: 'ascend' as SortOrder,
      sorter: (A: TableData, B: TableData) => {
        const sortColData = (data: TableData) => {
          const tags = data.tags.filter((tag) => isPerformanceTag(tag.type as EmployeeTagType))
          if (tags.length === 0) {
            return 0
          }
          return (
            tags
              .map((tag) => {
                if (tag.type === EmployeeTagType.Top) {
                  return tags.length > 1 ? -10 * tags.length : -5
                } else if (tag.type === EmployeeTagType.Expert) {
                  return -4
                } else if (tag.type === EmployeeTagType.Sales) {
                  return -3
                } else if (tag.type === EmployeeTagType.Speaker) {
                  return -2
                } else if (tag.type === EmployeeTagType.Coach) {
                  return -1
                } else if (tag.type === EmployeeTagType.Low || tag.type === EmployeeTagType.EntryLevelLow) {
                  return 1
                } else {
                  return 2
                }
              })
              .reduce((c, a) => c + a, 0) / Math.max(1, tags.length)
          )
        }
        return sortColData(A) - sortColData(B)
      },
      render: (rowData: TableData) => {
        return (
          <>
            {rowData.tags &&
              rowData.tags.length > 0 &&
              rowData.tags
                .filter((t) => isPerformanceTag(t.type as EmployeeTagType))
                .map((tag, index) => {
                  return (
                    <PerSpacer key={index} my="xxxs" display="inline-block">
                      <PerTag color={tagColor(tag.type)}>{t(`global.employeeTags.performance.${tag.type}`)}</PerTag>
                    </PerSpacer>
                  )
                })}
          </>
        )
      },
    },
    {
      key: 'impact-of-loss',
      title: t('rankingView.table.impactOfLoss.heading'),
      className: 'minWidth-column-m textAlign-left',
      sorter: (A: TableData, B: TableData) => {
        const sortColData = (data: TableData) => {
          const tags = data.tags.filter((tag) => isImpactOfLossTag(tag.type as EmployeeTagType))
          if (tags.length === 0) {
            return 0
          }
          return (
            tags
              .map((tag) => {
                if (tag.type === EmployeeTagType.ImpactOfLossCritical) {
                  return -4
                } else if (tag.type === EmployeeTagType.ImpactOfLossSignificant) {
                  return -3
                } else if (tag.type === EmployeeTagType.ImpactOfLossModerate) {
                  return -2
                } else if (tag.type === EmployeeTagType.ImpactOfLossMinor) {
                  return -1
                } else {
                  return 1
                }
              })
              .reduce((c, a) => c + a, 0) / Math.max(1, tags.length)
          )
        }
        return sortColData(A) - sortColData(B)
      },
      render: (rowData: TableData) => {
        return (
          <>
            {rowData.tags &&
              rowData.tags
                .filter((t) => isImpactOfLossTag(t.type as EmployeeTagType))
                .map((tag, index) => {
                  return (
                    <React.Fragment key={index}>
                      <PerTag color={tagColor(tag.type)}>{t(`global.employeeTags.impactOfLoss.${tag.type}`)}</PerTag>
                    </React.Fragment>
                  )
                })}
          </>
        )
      },
    },
    {
      key: 'risk-of-loss',
      title: t('rankingView.table.riskOfLoss.heading'),
      className: 'minWidth-column-m textAlign-left',
      sorter: (A: TableData, B: TableData) => {
        const sortColData = (data: TableData) => {
          const tags = data.tags.filter((tag) => isRiskOfLossTag(tag.type as EmployeeTagType))
          if (tags.length === 0) {
            return 0
          }
          return (
            tags
              .map((tag) => {
                if (tag.type === EmployeeTagType.RiskOfLossVeryHigh) {
                  return -4
                } else if (tag.type === EmployeeTagType.RiskOfLossHigh) {
                  return -3
                } else if (tag.type === EmployeeTagType.RiskOfLossMedium) {
                  return -2
                } else if (tag.type === EmployeeTagType.RiskOfLossLow) {
                  return -1
                } else {
                  return 1
                }
              })
              .reduce((c, a) => c + a, 0) / Math.max(1, tags.length)
          )
        }
        return sortColData(A) - sortColData(B)
      },
      render: (rowData: TableData) => {
        return (
          <>
            {rowData.tags &&
              rowData.tags
                .filter((t) => isRiskOfLossTag(t.type as EmployeeTagType))
                .map((tag, index) => {
                  return (
                    <React.Fragment key={index}>
                      <PerTag color={tagColor(tag.type)}>{t(`global.employeeTags.riskOfLoss.${tag.type}`)}</PerTag>
                    </React.Fragment>
                  )
                })}
          </>
        )
      },
    },
    {
      key: 'actions',
      title: t('rankingView.table.actions.heading'),
      className: 'textAlign-left',
      width: tableActionButtonsRowWidth(provideButtonsRowWidth()),
      render: (rowData: TableData) => {
        // TODO: this component was customized for phase 2, so it is no more compatible with phase 1.
        // That's why even if I'm restoring the original file I'm keeping expented props commented, since they are handled no more. I'm also adding new one "settingsTopPercentage".
        const employeeFullName = computeFullname(user.nameDisplayPreference, rowData.firstName, rowData.lastName)

        return (
          <RankingTableActionButtons
            employeeFullName={employeeFullName}
            isRankingEnabled
            employeeAndTagData={rowData}
            settingsTopPercentage={settings.topPercentage} // phase 2 specific
            setModalOpenRemoveLow={setModalOpenRemoveLow}
            setModalDataRemoveLow={setModalDataRemoveLow}
            setModalDataAddEntryLevelLow={setModalDataAddEntryLevelLow}
            setModalOpenTagHistory={(open: boolean) => {
              setModalOpenTagHistory(open)
              setModalTagHistoryEmployeeId(rowData.id)
            }}
            companyId={companyId}
            country={country}
            isCompanyManager={!!user.roles && user.roles.some((role) => RoleName.CompanyManager === role)}
            isYourCompanyView
            isHRSettings
            isAllCompaniesSettings
          />
        )
      },
    },
  ]

  return (
    <>
      {modalDataRemoveLow && (
        <CompanySettingsRankingRemoveLowTagModal
          isOpen={modalOpenRemoveLow}
          onSubmit={removeEmployeeTag}
          modalData={modalDataRemoveLow}
          onCancel={onRemoveLowModalCancel}
        />
      )}
      {modalDataAddEntryLevelLow && (
        <AddEntryLevelLowTagAssignmentModal
          onClose={onAddEntryLevelLowModalCancel}
          employeeFullName={computeFullname(
            user.nameDisplayPreference,
            modalDataAddEntryLevelLow.firstName,
            modalDataAddEntryLevelLow.lastName,
          )}
          employeeFirstName={modalDataAddEntryLevelLow.firstName}
          employeeLastName={modalDataAddEntryLevelLow.lastName}
          employeeHasFutureEmploymentChange={employeeHasFutureEmploymentChange}
          employeeHasOngoingAppraisal={employeeHasOngoingAppraisal}
          companyId={companyId}
          country={country}
          employeeId={modalDataAddEntryLevelLow.id}
        />
      )}
      {modalTagHistoryEmployeeId && (
        <LazyRankingTagHistoryModal
          isOpen={modalOpenTagHistory}
          onCancel={onTagHistoryModalCancel}
          employee={tableData.filter((e) => e.id === modalTagHistoryEmployeeId)[0] as EmployeeDisplayTinyInfo}
        />
      )}

      <Table
        className={classNames({
          'light small scroll-compensation viewWithTabsAndInfoBox': !isMessageBoxVisible,
          'light small scroll-compensation viewWithTabsAndInfoBoxAndMessageBox': isMessageBoxVisible,
          'light small scroll-compensation viewWithTabsAndInfoBoxAndConfirmButton': !!settings.tagAssignmentIsInProgress,
          'light small scroll-compensation viewWithTabsAndInfoBoxAndMessageBoxAndConfirmButton':
            !!settings.tagAssignmentIsInProgress && isMessageBoxVisible,
        })}
        dataSource={tableData.map((o) => ({ ...o, key: o.id }))}
        columns={columns}
        pagination={false}
        scroll={{ y: true }}
        locale={{
          emptyText: settings.rankingIsDisabled ? (
            <PerEmpty
              icon
              heading={t('rankingView.table.disabled.headingWithoutYear')}
              info={isCompanySettings && isHr ? t('rankingView.table.disabled.infoWithoutYear') : undefined}
              button={
                isCompanySettings &&
                isHr && (
                  <PerButton
                    text={t('rankingView.table.disabled.btn')}
                    onClick={() => updateRankingDisabled(false)}
                    color="success"
                    before="check_stroke"
                  />
                )
              }
            />
          ) : (
            <PerEmpty heading={t('rankingView.table.empty.heading')} />
          ),
        }}
      />
    </>
  )
}
